import { useLayoutEffect, useRef, useState } from "react";
import { LayoutNode } from "../layout";

/**
 * Плавная перестройка раскладки: при раскрытии, свёртке, переносе и поиске узлы
 * переезжают на новые места, новые выезжают из-за родителя, исчезающие уезжают
 * в родителя и гаснут. Анимируются только координаты (кадр — новый массив позиций);
 * прозрачность и масштаб появления — CSS-анимацией самих карточек.
 */

export interface AnimatedNode {
    layout: LayoutNode;
    x: number;
    y: number;
    leaving: boolean;
    /** задержка CSS-появления: каскадом сверху вниз */
    delay: number;
}

interface Point {
    x: number;
    y: number;
}

const DURATION = 420;

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const reducedMotion = () =>
    typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function useAnimatedLayout(target: LayoutNode[]): AnimatedNode[] {
    const shown = useRef(new Map<string, Point>());
    const prev = useRef(new Map<string, LayoutNode>());
    const raf = useRef(0);
    const [frame, setFrame] = useState<AnimatedNode[]>([]);

    useLayoutEffect(() => {
        cancelAnimationFrame(raf.current);

        const targetMap = new Map(target.map((n) => [n.id, n]));
        const first = shown.current.size === 0;
        const from = new Map<string, Point>();
        const delays = new Map<string, number>();

        let appearIndex = 0;
        target.forEach((n) => {
            const current = shown.current.get(n.id);
            if (current) {
                from.set(n.id, current);
                delays.set(n.id, 0);
                return;
            }

            // новый узел выезжает из-за родителя
            const parentPos = n.parentId ? shown.current.get(n.parentId) : undefined;
            const parent = n.parentId ? targetMap.get(n.parentId) : undefined;
            from.set(
                n.id,
                parentPos && parent && !first
                    ? { x: parentPos.x + parent.w - n.w, y: parentPos.y + parent.h / 2 - n.h / 2 }
                    : { x: n.x, y: n.y }
            );
            delays.set(n.id, first ? Math.min(n.depth * 70, 280) : Math.min(appearIndex++ * 14, 180));
        });

        // уходящие узлы уезжают в ближайшего оставшегося предка
        const leaving: { node: LayoutNode; from: Point; to: Point }[] = [];
        prev.current.forEach((n, id) => {
            if (targetMap.has(id)) {
                return;
            }
            let anchorId = n.parentId;
            while (anchorId && !targetMap.has(anchorId)) {
                anchorId = prev.current.get(anchorId)?.parentId ?? null;
            }
            const anchor = anchorId ? targetMap.get(anchorId) : undefined;
            const start = shown.current.get(id) ?? { x: n.x, y: n.y };
            leaving.push({
                node: n,
                from: start,
                to: anchor ? { x: anchor.x + anchor.w - n.w, y: anchor.y + anchor.h / 2 - n.h / 2 } : start,
            });
        });

        prev.current = targetMap;

        const render = (t: number) => {
            const k = easeOutCubic(t);
            const next = new Map<string, Point>();
            const nodes: AnimatedNode[] = target.map((n) => {
                const a = from.get(n.id)!;
                const p = { x: a.x + (n.x - a.x) * k, y: a.y + (n.y - a.y) * k };
                next.set(n.id, p);
                return { layout: n, x: p.x, y: p.y, leaving: false, delay: delays.get(n.id) ?? 0 };
            });

            if (t < 1) {
                leaving.forEach(({ node, from: a, to: b }) => {
                    nodes.unshift({
                        layout: node,
                        x: a.x + (b.x - a.x) * k,
                        y: a.y + (b.y - a.y) * k,
                        leaving: true,
                        delay: 0,
                    });
                });
            }

            shown.current = next;
            setFrame(nodes);
        };

        const moving = target.some((n) => {
            const a = from.get(n.id)!;
            return Math.abs(a.x - n.x) > 0.5 || Math.abs(a.y - n.y) > 0.5;
        });

        if (first || reducedMotion() || (!moving && leaving.length === 0)) {
            render(1);
            return;
        }

        const start = performance.now();
        const tick = (now: number) => {
            const t = Math.min(1, (now - start) / DURATION);
            render(t);
            if (t < 1) {
                raf.current = requestAnimationFrame(tick);
            }
        };
        raf.current = requestAnimationFrame(tick);

        return () => cancelAnimationFrame(raf.current);
    }, [target]);

    return frame;
}
